import React, { useState } from 'react';
import {
  DataTable,
  DataTableExpandedRows,
  DataTablePageEvent,
} from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import {
  SalaryHistoryDbType,
  SalaryHistoryTravelItem,
  SalaryItemRate,
} from '../../../../api/types/accountants/salary-history/salary-history.type';
import { LegalEntityType } from '../../../user-page/roles/lecturer-price-rate/componenets/acountant/user-page-lecturer-rate-accountant';
import dateUtils from '../../../../utils/date-format';
import { SalaryExportDialog } from './salary-export-dialog';
import { LecturerRateDisplayType } from '../../../../api/types/lecturers/price-rate/lecturer-rate-display-type';
import { WarningDialog } from '../../../../component/ui/warning-dialog/warning-dialog';
import { deleteSalaryHistoryOperation } from '../operations/delete-salary-history.operation';
import {
  formatHourUnits,
  getItemBilledQuantity,
  getSalaryBreakHourUnitsTotal,
  getSalaryTotalWithBreakDeduction,
} from '../utils/salary-break-calculations';

interface SalaryHistoryTableProps {
  salariesData: SalaryHistoryDbType[];
  totalRecords: number;
  first: number;
  rows: number;
  loading?: boolean;
  onPage: (e: DataTablePageEvent) => void;
  onRefresh?: () => void;
  startDate: Date | null;
  endDate: Date | null;
  branchId: number | null;
}

export const SalaryHistoryTable: React.FC<SalaryHistoryTableProps> = ({
  salariesData,
  totalRecords,
  first,
  rows,
  loading = false,
  onPage,
  onRefresh,
  startDate,
  endDate,
  branchId,
}) => {
  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows>({});
  const [selectedRows, setSelectedRows] = useState<SalaryHistoryDbType[]>([]);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [salaryPendingDelete, setSalaryPendingDelete] =
    useState<SalaryHistoryDbType | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS',
    }).format(value);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('he-IL').format(new Date(date));
  };

  const nameTemplate = (rowData: SalaryHistoryDbType) => {
    return `${rowData.firstName} ${rowData.lastName}`;
  };

  /** כמות לחיוב: שעה מלאה = שעות שעון; אקדמית = יחידות של 45 דק׳ (כמו סכום השורה) */
  const itemBilledQuantity = (item: SalaryItemRate) =>
    getItemBilledQuantity(item);

  const totalAmountTemplate = (rowData: SalaryHistoryDbType) => {
    const itemsTotal = rowData.items.reduce(
      (sum, item) =>
        sum +
        item.rate *
          item.hoursNumber *
          (item.displayType === LecturerRateDisplayType.OneHour ? 1 : 4 / 3),
      0,
    );
    const travelTotal = (rowData.travelItems ?? []).reduce(
      (sum, t) => sum + (Number(t.amount) || 0),
      0,
    );
    return formatCurrency(itemsTotal + travelTotal);
  };

  const itemsCountTemplate = (rowData: SalaryHistoryDbType) => {
    return rowData.items.length;
  };

  // Helper function to get unique legal entity types for a user
  const getLegalEntityTypesTemplate = (rowData: SalaryHistoryDbType) => {
    const uniqueTypes = [
      ...new Set(rowData.items.map((item) => item.legalEntityType)),
    ];
    if (uniqueTypes.length === 1) {
      return LegalEntityType.getDisplayText(uniqueTypes[0]);
    }
    return uniqueTypes
      .map((type) => LegalEntityType.getDisplayText(type))
      .join(', ');
  };

  // Helper function to get unique reporting codes for a user
  const getReportingCodesTemplate = (rowData: SalaryHistoryDbType) => {
    const uniqueCodes = [
      ...new Set(rowData.items.map((item) => item.accountingReportCode)),
    ];
    return uniqueCodes.join(', ');
  };

  // Helper function to get unique legal entity IDs for a user
  const getLegalEntityIdsTemplate = (rowData: SalaryHistoryDbType) => {
    const uniqueIds = [
      ...new Set(rowData.items.map((item) => item.legalEntityId)),
    ];
    return uniqueIds.join(', ');
  };

  // Helper function for sorting by legal entity type
  const getLegalEntityTypeForSorting = (rowData: SalaryHistoryDbType) => {
    // Get the most common legal entity type, or the first one if all are unique
    const types = rowData.items.map((item) => item.legalEntityType);
    const typeCount = types.reduce(
      (acc, type) => {
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      },
      {} as Record<LegalEntityType, number>,
    );

    const mostCommonType = Object.entries(typeCount).sort(
      ([, a], [, b]) => b - a,
    )[0][0] as LegalEntityType;
    return LegalEntityType.getDisplayText(mostCommonType);
  };

  const expandedRowTemplate = (data: SalaryHistoryDbType) => {
    const travelItems = data.travelItems ?? [];

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
          paddingRight: '8px',
        }}
      >
        <div>
          <h5 style={{ display: 'flex', justifyContent: 'right' }}>
            פירוט תשלומים
          </h5>
          <DataTable value={data.items} responsiveLayout="scroll" size="small">
            <Column field="id" header="מזהה" style={{ textAlign: 'right' }} />
            <Column
              field="rate"
              header="תעריף"
              body={(rowData: SalaryItemRate) => formatCurrency(rowData.rate)}
              style={{ textAlign: 'right' }}
            />

            <Column
              field="rate"
              header="כמות"
              body={(rowData: SalaryItemRate) => {
                const qty = itemBilledQuantity(rowData);
                const formatted =
                  qty % 1 === 0 ? qty.toString() : qty.toFixed(2);
                return <label>{formatted} שעות</label>;
              }}
              style={{ textAlign: 'right' }}
            />

            <Column
              field="rate"
              header="תאריך"
              body={(rowData: SalaryItemRate) => formatDate(rowData.startAt)}
              style={{ textAlign: 'right' }}
            />
            <Column
              field="breakMinutes"
              header="דקות הפסקה"
              body={(rowData: SalaryItemRate) =>
                rowData.breakMinutes !== null &&
                rowData.breakMinutes !== undefined
                  ? rowData.breakMinutes
                  : '—'
              }
              style={{ textAlign: 'right' }}
            />
            <Column
              field="activityType"
              header="סוג פעילות"
              style={{ textAlign: 'right' }}
            />
            <Column
              field="legalEntityType"
              header="סוג ישות משפטית"
              style={{ textAlign: 'right' }}
              body={(rowDate: SalaryItemRate) => (
                <div>
                  {LegalEntityType.getDisplayText(rowDate.legalEntityType)}
                </div>
              )}
            />
            <Column
              field="accountingReportCode"
              header="קוד דיווח במערכת שכר"
              style={{ textAlign: 'right' }}
            />
            <Column
              field="createdByName"
              header="מפיק משכורת"
              style={{ textAlign: 'right' }}
            />
            <Column
              field="createdAt"
              header="תאריך יצירה"
              body={(rowData: SalaryItemRate) =>
                dateUtils.displayFormatDate(rowData.createdAt)
              }
              style={{ textAlign: 'right' }}
            />
          </DataTable>
        </div>

        <div>
          <h5 style={{ display: 'flex', justifyContent: 'right' }}>נסיעות</h5>
          <DataTable
            value={travelItems}
            responsiveLayout="scroll"
            size="small"
            emptyMessage={<div style={{ textAlign: 'right' }}>אין נסיעות</div>}
          >
            <Column field="id" header="מזהה" style={{ textAlign: 'right' }} />
            <Column
              field="travelDate"
              header="תאריך נסיעה"
              body={(rowData: SalaryHistoryTravelItem) =>
                formatDate(rowData.travelDate)
              }
              style={{ textAlign: 'right' }}
            />
            <Column
              field="amount"
              header="סכום"
              body={(rowData: SalaryHistoryTravelItem) =>
                formatCurrency(rowData.amount)
              }
              style={{ textAlign: 'right' }}
            />
            <Column
              field="accountingTravelReportCode"
              header="קוד דיווח נסיעות"
              body={(rowData: SalaryHistoryTravelItem) =>
                rowData.accountingTravelReportCode ?? '—'
              }
              style={{ textAlign: 'right' }}
            />
            <Column
              field="legalEntityType"
              header="סוג ישות משפטית"
              body={(rowData: SalaryHistoryTravelItem) =>
                rowData.legalEntityType
                  ? LegalEntityType.getDisplayText(rowData.legalEntityType)
                  : '—'
              }
              style={{ textAlign: 'right' }}
            />
            <Column
              field="createdByName"
              header="מפיק"
              style={{ textAlign: 'right' }}
            />
            <Column
              field="createdAt"
              header="תאריך יצירה"
              body={(rowData: SalaryHistoryTravelItem) =>
                dateUtils.displayFormatDate(rowData.createdAt)
              }
              style={{ textAlign: 'right' }}
            />
          </DataTable>
        </div>
      </div>
    );
  };

  const expanderTemplate = (rowData: SalaryHistoryDbType) => {
    const isExpanded = expandedRows[rowData.id] !== undefined;
    return (
      <button
        type="button"
        onClick={() => {
          const newExpandedRows = { ...expandedRows };
          if (isExpanded) {
            delete newExpandedRows[rowData.id];
          } else {
            newExpandedRows[rowData.id] = true;
          }
          setExpandedRows(newExpandedRows);
        }}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: '14px',
          padding: '4px',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {isExpanded ? '▼' : '▶'}
      </button>
    );
  };

  const handleExportClick = () => {
    if (salariesData.length === 0) {
      return;
    }
    setShowExportDialog(true);
  };

  const handleConfirmDeleteSalary = async () => {
    if (!salaryPendingDelete) {
      return;
    }
    setDeleteLoading(true);
    const ok = await deleteSalaryHistoryOperation(salaryPendingDelete.id);
    setDeleteLoading(false);
    if (ok) {
      setSalaryPendingDelete(null);
      setSelectedRows((prev) =>
        prev.filter((r) => r.id !== salaryPendingDelete.id),
      );
      onRefresh?.();
    }
  };

  const deleteActionTemplate = (rowData: SalaryHistoryDbType) => (
    <Button
      type="button"
      icon="pi pi-trash"
      rounded
      text
      severity="danger"
      aria-label="מחק משכורת"
      onClick={(e) => {
        e.stopPropagation();
        setSalaryPendingDelete(rowData);
      }}
    />
  );

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
        }}
      >
        <h2>היסטוריית משכורות</h2>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>
            נבחרו {selectedRows.length} מתוך {totalRecords} רשומות
          </span>
          <Button
            label="יצוא לאקסל"
            icon="pi pi-file-excel"
            onClick={handleExportClick}
            disabled={salariesData.length === 0}
            style={{
              backgroundColor: salariesData.length > 0 ? '#10B981' : '#9CA3AF',
              border: 'none',
              padding: '0.5rem 1rem',
            }}
          />
        </div>
      </div>

      <DataTable
        value={salariesData}
        loading={loading}
        expandedRows={expandedRows}
        onRowToggle={(e) => setExpandedRows(e.data as DataTableExpandedRows)}
        selection={selectedRows}
        onSelectionChange={(e) => setSelectedRows(e.value)}
        selectionMode="multiple"
        responsiveLayout="scroll"
        dataKey="id"
        rowExpansionTemplate={expandedRowTemplate}
        tableStyle={{ minWidth: '80rem' }}
        emptyMessage={<div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>}
        paginator
        lazy
        first={first}
        rows={rows}
        totalRecords={totalRecords}
        onPage={onPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
      >
        <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
        <Column
          expander={false}
          body={expanderTemplate}
          style={{ width: '3rem', textAlign: 'center' }}
        />
        <Column
          header="שם המרצה"
          body={nameTemplate}
          sortable
          style={{ textAlign: 'right' }}
        />
        <Column
          field="nationalId"
          header="תעודת זהות"
          sortable
          style={{ textAlign: 'right' }}
        />
        <Column
          field="email"
          header="דוא״ל"
          sortable
          style={{ textAlign: 'right' }}
        />
        <Column
          header="סוג ישות משפטית"
          body={getLegalEntityTypesTemplate}
          sortable
          sortFunction={(event) => {
            const data = [...event.data];
            return data.sort((a, b) => {
              const aType = getLegalEntityTypeForSorting(a);
              const bType = getLegalEntityTypeForSorting(b);
              return event.order === 1
                ? aType.localeCompare(bType, 'he')
                : bType.localeCompare(aType, 'he');
            });
          }}
          style={{ textAlign: 'right', minWidth: '150px' }}
        />
        <Column
          header="קוד דיווח"
          body={getReportingCodesTemplate}
          sortable
          style={{ textAlign: 'right', minWidth: '120px' }}
        />
        <Column
          header="מזהה ישות משפטית"
          body={getLegalEntityIdsTemplate}
          sortable
          style={{ textAlign: 'right', minWidth: '150px' }}
        />
        <Column
          header="מספר תשלומים"
          body={itemsCountTemplate}
          sortable
          style={{ textAlign: 'right' }}
        />
        <Column
          header="סכום כולל"
          body={totalAmountTemplate}
          sortable
          style={{ textAlign: 'right' }}
        />
        <Column
          header="סה״כ הפסקות"
          body={(rowData: SalaryHistoryDbType) =>
            formatHourUnits(getSalaryBreakHourUnitsTotal(rowData.items))
          }
          sortable
          style={{ textAlign: 'right' }}
        />
        <Column
          header="סה״כ כולל הפסקות"
          body={(rowData: SalaryHistoryDbType) =>
            formatCurrency(
              getSalaryTotalWithBreakDeduction(
                rowData.items,
                rowData.travelItems,
              ),
            )
          }
          sortable
          style={{ textAlign: 'right' }}
        />
        <Column
          field="createdAt"
          header="תאריך יצירה"
          body={(rowData: SalaryHistoryDbType) => formatDate(rowData.createdAt)}
          sortable
          style={{ textAlign: 'right' }}
        />
        <Column
          header="פעולות"
          body={deleteActionTemplate}
          style={{ width: '4rem', textAlign: 'center' }}
        />
      </DataTable>

      <WarningDialog
        header="מחיקת משכורת"
        isVisible={salaryPendingDelete !== null}
        onHide={() => {
          if (!deleteLoading) {
            setSalaryPendingDelete(null);
          }
        }}
        addIcon
        content="פעולה זו תמחק את רישום המשכורת לצמיתות. לא ניתן לבטל."
        requiredTypeingToApproved="delete"
        notInformation={
          salaryPendingDelete
            ? `מרצה: ${salaryPendingDelete.firstName} ${salaryPendingDelete.lastName}\nמזהה משכורת: ${salaryPendingDelete.id}`
            : ''
        }
        notInformationVariant="yellow"
        confirmLabel="מחק"
        confirmLoading={deleteLoading}
        onConfirm={handleConfirmDeleteSalary}
        cancelLabel="ביטול"
      />

      <SalaryExportDialog
        visible={showExportDialog}
        onHide={() => setShowExportDialog(false)}
        exportRows={salariesData}
        startDate={startDate}
        endDate={endDate}
        branchId={branchId}
      />
    </div>
  );
};
